import { BanIcon, CheckCircleIcon, ClipboardCopyIcon, DotsCircleHorizontalIcon, DotsVerticalIcon, ExclamationCircleIcon, RefreshIcon, TrashIcon } from "@heroicons/react/outline";
import { WorkflowAutomationItem } from "../interfaces";
import { BLOCK_ICON_MAP, MIMETYPE_ICON_MAP, isItemActionable, OutputDetailRouteState, resolveMicrosoftMimeType } from "../utils";
import moment from "moment";
import { useContext, useEffect, useState } from "react";
import { WorkflowContext, useModal } from "../contexts";
import { OAuthError, deleteData, getData, postData } from "../backend";
import { Popover, Transition } from "@headlessui/react";
import OAuthModal from "./OAuthModal";
import CopyToClipboard from "react-copy-to-clipboard";
import { Link, useHistory } from "react-router-dom";
import { preload } from "swr";
import useUser from "../hooks/useUser";
import ErrorModal from "./ErrorModal";
import { useTranslation } from "react-i18next";
import HeroIcon from "./HeroIcon";

interface Props {
  item: WorkflowAutomationItem;
  lastViewedTime: number;
  hidePreview?: boolean;
  showWorkflow?: boolean;
  refreshOuputs: () => void;
  allItemIds: string[];
  actionableItemIds: string[];
}

function WorkflowOutputsItem(props: Props) {
  // Preload item detail + workflow data so navigation feels instant
  useEffect(() => {
    preload(`/workflows/automation-items/${props.item.id}`, getData);
    preload(`/workflows/${props.item.workflow.id}/`, getData);
  }, [props.item.id, props.item.workflow.id]);

  const { t } = useTranslation();

  const { hasPermission } = useUser();

  const history = useHistory();
  const { user } = useUser();

  const [actionStatus, setActionStatus] = useState<"INITIAL" | "DELETED" | "RERUNNING">("INITIAL");

  const [showCopiedMessage, setShowCopiedMessage] = useState(false);
  const [item, setItem] = useState(props.item)
  const { outputs, outputEmails } = item;

  const { openModal } = useModal();

  const { workflow: currentWorkflow } = useContext(WorkflowContext);

  // const allTrackingPixels: Array<TrackingPixel> = [...item.signatureRequests.map(s => s.trackingPixel), ...item.outputEmails.map(e => e.trackingPixel)].filter(Boolean);
  // const allTrackingPixelEvents: Array<TrackingPixelEvent> = allTrackingPixels.map(p => p.events).flat();

  const actionable = isItemActionable(item, user?.id ?? '', currentWorkflow?.review);
  const showReview = actionable && item.stage === "REVIEW_PENDING";
  const showSigAction = actionable && ["SIGNATURES_REQUEST", "SIGNATURES_PENDING"].includes(item.stage);

  useEffect(() => setItem(props.item), [props.item]);

  function handleDeleteItem() {
    setActionStatus("DELETED");
    deleteData(`/workflows/automation-items/${item.id}/`)
      .catch(() => setActionStatus("INITIAL"));
  }

  function handleRerunItem() {
    setActionStatus("RERUNNING");
    postData(`/workflows/automation-items/${item.id}/rerun/`)
      .catch(error => {
        setActionStatus("INITIAL");
        if (error instanceof OAuthError)
          openModal(<OAuthModal oauthUrl={error.oauthUrl} />);
        else
          openModal(<ErrorModal details={error} />);
      });
  }

  function handleRescindItem() {
    setActionStatus("RERUNNING");

    postData(`/workflows/automation-items/${item.id}/rescind/`)
      .then(setItem)
      .catch(console.error)
      .finally(() => setActionStatus("INITIAL"));
  }

  function handleOnCopySupport() {
    setShowCopiedMessage(true);
    setTimeout(() => setShowCopiedMessage(false), 2000);
  }

  const detailPath = props.showWorkflow
    ? `/outputs/${item.id}`
    : `/w/${item.workflow.id}/outputs/${item.id}`;
  const listPath = props.showWorkflow
    ? '/outputs'
    : `/w/${item.workflow.id}/outputs`;

  function navigateToDetail(isAction: boolean) {
    const ids = isAction ? props.actionableItemIds : props.allItemIds;
    const state: OutputDetailRouteState = {
      sessionItemIds: ids,
      entryIndex: ids.indexOf(item.id),
      basePath: listPath,
    };
    history.push(detailPath, state);
  }

  // The same output can be stored in several destinations (Google Drive, OneDrive, HubSpot), so show at most
  // one "original" and one PDF entry per output rather than one per destination
  const originalOutput = (output: typeof outputs[number]): { name?: string, icon: string } | undefined => {
    if (output.file && !item.byServiceAccount) return { name: output.file.name, icon: MIMETYPE_ICON_MAP[output.file.mimeType ?? "application/vnd.google-apps.document"] };
    if (output.onedriveFile) return { name: output.onedriveFile.fileName, icon: MIMETYPE_ICON_MAP[resolveMicrosoftMimeType(output.onedriveFile.fileMimeType, output.onedriveFile.fileName)] };
    if (output.fileMicrosoft && !item.byServiceAccount) return { name: output.fileMicrosoft.name, icon: MIMETYPE_ICON_MAP[resolveMicrosoftMimeType(output.fileMicrosoft.mimeType, output.fileMicrosoft.name)] };
    if (output.hubspotFileMicrosoft) return { name: output.hubspotFileMicrosoft.fileName, icon: MIMETYPE_ICON_MAP[resolveMicrosoftMimeType(undefined, output.hubspotFileMicrosoft.fileName, output.hubspotFileMicrosoft.fileExt)] };
  };

  const pdfOutput = (output: typeof outputs[number]): { name?: string, icon: string } | undefined => {
    const name = output.filePdf?.name ?? output.onedrivePdf?.fileName ?? output.hubspotFilePdf?.fileName;
    if (name) return { name, icon: MIMETYPE_ICON_MAP["application/pdf"] };
  };

  const itemOutputs = [
    ...outputs.map(originalOutput),
    ...outputs.map(pdfOutput),
    // Fallback for outputs with no file FKs populated (e.g. PDF_FILLABLE where only preview_pdf_bucket_token / output_name is set)
    ...outputs
      .filter(o => !o.file && !o.onedriveFile && !o.filePdf && !o.fileMicrosoft && !o.onedrivePdf && !o.hubspotFilePdf && !o.hubspotFileMicrosoft)
      .map(output => ({ name: output.outputName, icon: MIMETYPE_ICON_MAP["application/pdf"] })),
    ...outputEmails.map(email => ({ name: email.emailSubject, icon: BLOCK_ICON_MAP[email.emailType] }))
  ].filter((item): item is { name: string, icon: string } => !!item?.name);

  const [primaryOutput, ...remainingOutputs] = itemOutputs;

  const hasDraftEmails = outputEmails.reduce((acc, email) => acc || email.status === "PENDING", false);

  return (
    <>
      <div className={`flex items-center text-sm font-semibold font-gilroy text-gray-600 cursor-pointer h-14 last:border-b-0 border-b border-gray-200`} onClick={() => navigateToDetail(false)}>

        {/* Indicator */}
        <div className="flex items-center justify-start w-4 flex-shrink-0">
          {props.lastViewedTime < new Date(item.updatedAt).getTime() &&
            <div className="bg-green w-2 h-2 rounded-full" />
          }
        </div>

        {/* Filename */}
        <div className="flex items-center gap-2 group w-full min-w-[300px]">
          {(!primaryOutput && item.status === 'PENDING') &&
            <div className="text-gray-500 font-semibold">
              {t("In progress...")}
            </div>
          }

          {(!primaryOutput && item.status !== 'PENDING') &&
            <div className="text-yellow font-semibold flex items-center gap-4">
              {t("No outputs or emails were created for this automation item")}
              {item.errorReason &&
                <div className="underline text-gray-600">View error details</div>
              }
            </div>
          }

          {primaryOutput &&
            <span className="flex items-center gap-1">
              <img src={primaryOutput.icon} className="w-4 h-4" alt="Output Icon" />
              <div className={`max-w-[300px] truncate ${actionStatus !== "INITIAL" ? "text-gray-400 line-through" : "text-gray-600"}`}>
                {primaryOutput.name}
              </div>
            </span>
          }

          {remainingOutputs.length > 0 &&
            <div className="ml-1 text-gray-400">+</div>
          }

          {remainingOutputs.map((output, index) => <img key={index} src={output?.icon} className="w-4 h-4" alt="Output Icon" />)}
        </div>

        {/* Tracking */}
        {/* <div className="h-14 w-20">
          {(allTrackingPixels.length > 0 && allTrackingPixelEvents.length === 0) &&
            <div className="relative group cursor-default">
              <EyeIcon className="w-6 h-6 text-gray-400 mx-auto" />
              <div className="hidden group-hover:block absolute bg-white shadow-lg text-center w-72 -right-32 top-6 z-10 p-2 rounded border border-gray-200">
                {t("No views yet")}
              </div>
            </div>
          }

          {(allTrackingPixels.length > 0 && allTrackingPixelEvents.length > 0) &&
            <div className="relative group cursor-default">
              <EyeIcon className="w-6 h-6 text-blue mx-auto" />
              <div className="hidden group-hover:block absolute bg-white shadow-lg text-center w-72 -right-32 top-6 z-10 p-2 rounded border border-gray-200">
                <span className="font-semibold">{allTrackingPixelEvents.length}</span>{allTrackingPixelEvents.length >= 2 ? t("views") : t("view")}, last viewed {moment(allTrackingPixelEvents[0].createdAt).fromNow()}
              </div>
            </div>
          }
        </div> */}


        {/* Workflow column */}
        {props.showWorkflow &&
          <Link to={item.workflow.previewSourceType === "SIGNATURES" ? `/s/${item.workflow.id}` : `/w/${item.workflow.id}/outputs`} className="flex items-center w-60 flex-shrink-0 gap-2 group">
            <div className="flex items-center justify-center w-6 h-6 text-white rounded cursor-pointer flex-shrink-0" style={{ backgroundColor: item.workflow.color }}>
              <HeroIcon icon={item.workflow.icon} className="w-4 h-4" />
            </div>
            <h3 className="font-semibold font-gilroy truncate text-gray-600 cursor-pointer group-hover:underline">
              {item.workflow.name}
            </h3>
          </Link>
        }

        {/* Running user */}
        {(!props.showWorkflow && !!currentWorkflow.team && item.initiatingUser) &&
          <div className="flex items-center w-60 flex-shrink-0 gap-2">
            {t("Ran by")}&nbsp;<span className="text-gray-600">{item.initiatingUser.fullName}</span>
          </div>
        }

        {/* Status */}
        <div className="flex items-center w-40 flex-shrink-0">
          {actionStatus === "INITIAL" &&
            <>
              {item.status === "PENDING" && <DotsCircleHorizontalIcon className="w-6 h-6 text-blue mx-auto" />}
              {item.status === "COMPLETED" &&
                <>
                  {hasDraftEmails
                    ? <div className="btn btn-white text-xs py-1 mx-auto px-2 w-28" onClick={(e) => { e.stopPropagation(); navigateToDetail(true); }}>{t("Email drafts")}</div>
                    : showSigAction
                      ? <button className="btn btn-blue text-xs py-1 mx-auto px-2 w-28" onClick={(e) => { e.stopPropagation(); navigateToDetail(true); }}>{t("Review")}</button>
                      : <CheckCircleIcon className="w-6 h-6 text-green mx-auto" />
                  }
                </>
              }
              {item.status === "COMPLETED_PARTIALLY" &&
                <>
                  {showReview || showSigAction
                    ? <button className="btn btn-blue text-xs py-1 mx-auto px-2 w-28" disabled={!hasPermission(currentWorkflow.team, "workflows.run")} onClick={(e) => { e.stopPropagation(); navigateToDetail(true); }}>{t("Review")}</button>
                    : <DotsCircleHorizontalIcon className="w-6 h-6 text-blue mx-auto" />
                  }
                </>
              }
              {item.status === "COMPLETED_WITH_ERROR" && <ExclamationCircleIcon className="w-6 h-6 text-yellow mx-auto" />}
            </>
          }

          {actionStatus === "RERUNNING" &&
            <div className="text-gray-400">
              {t("Rerunning...")}
            </div>
          }
        </div>

        {/* Created at */}
        <div className="flex items-center justify-center w-40 flex-shrink-0">
          {actionStatus === "DELETED" && t("Removed")}

          {actionStatus === "INITIAL" &&
            moment(props.item.createdAt).format("MMM D, h:mma")
          }
        </div>

        {/* Additional options (kebeb menu) */}
        <div className="flex justify-center items-center w-10 flex-shrink-0">
          {actionStatus === "INITIAL" &&
            <div className="flex justify-center relative">
              <Transition
                enter="transition duration-100 ease-out"
                enterFrom="transform scale-95 opacity-0"
                enterTo="transform scale-100 opacity-100"
                leave="transition duration-75 ease-out"
                leaveFrom="transform scale-100 opacity-100"
                leaveTo="transform scale-95 opacity-0"
                show={showCopiedMessage}
                unmount
              >
                <div className="absolute bg-blue whitespace-nowrap text-white rounded top-0 right-44 z-10 shadow px-2 -pb-1 pointer-events-none">
                  {t("Support code copied to clipboard")}
                </div>
              </Transition>
              <Popover className="relative" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                <Popover.Button>
                  <DotsVerticalIcon className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                </Popover.Button>
                <Popover.Panel className="absolute mt-1 right-0 z-20 flex flex-col w-42 bg-white border rounded shadow overflow-y-auto">
                  {(currentWorkflow.source?.sourceType !== "SIGNATURE_LINK" && hasPermission(currentWorkflow.team, "workflows.run")) &&
                    <Popover.Button className="flex items-center font-medium text-xs text-blue w-full px-4 py-2 hover:bg-blue-50 cursor-pointer whitespace-nowrap" onClick={handleRerunItem}>
                      <RefreshIcon className="w-4 h-4 mr-2" /> {t("Rerun")}
                    </Popover.Button>
                  }
                  {hasPermission(currentWorkflow.team, "workflows.edit") && // TODO: Should this be workflows.run?
                    <Popover.Button className="flex items-center font-medium text-xs text-blue w-full px-4 py-2 hover:bg-blue-50 cursor-pointer whitespace-nowrap" onClick={handleDeleteItem}>
                      <TrashIcon className="w-4 h-4 mr-2" /> {t("Delete")}
                    </Popover.Button>
                  }
                  <CopyToClipboard text={item.id} onCopy={handleOnCopySupport}>
                    <Popover.Button className="flex items-center font-medium text-xs text-blue w-full px-4 py-2 hover:bg-blue-50 cursor-pointer whitespace-nowrap">
                      <ClipboardCopyIcon className="w-4 h-4 mr-2" /> {t("Copy Support Code")}
                    </Popover.Button>
                  </CopyToClipboard>
                  {(currentWorkflow.review && currentWorkflow.signatureConfig && item.stage === "SIGNATURES_PENDING") &&
                    <Popover.Button className="flex items-center font-medium text-xs text-blue w-full px-4 py-2 hover:bg-blue-50 cursor-pointer whitespace-nowrap" onClick={handleRescindItem}>
                      <BanIcon className="w-4 h-4 mr-2" /> {t("Cancel Signature Requests")}
                    </Popover.Button>
                  }
                </Popover.Panel>
              </Popover>
            </div>
          }
        </div>
      </div>


    </>
  );
}

export default WorkflowOutputsItem;
